import { Modal, notification } from "antd";
import { find, get } from "lodash";
import { useRef } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { MdCheckCircle, MdCloudDownload, MdSend } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Alert from "shared/components/Alert";
import Button from "shared/components/Button";
import Form from "shared/components/Form";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { useScreenActionPermissions } from "shared/hooks/usePermissions";
import { NAME_SPACES } from "shared/locales/constants";
import format from "string-template";
import { SEND_TO_MANAGEMENT } from "utils/api/graphql/mutations/subscription";
import {
  GET_MANAGEMENT_ATTACHMENTS,
  SUBSCRIPTION,
} from "utils/api/graphql/queries/subscription";
import {
  ACTIONS,
  CONTACT_POINT_SYSTEM,
  DATE_TIME_FORMAT,
  MODULES,
  SCREENS,
  SEND_TO_MANAGEMENT_SEND_STATUSES,
  SUBMODULES,
} from "utils/constants";
import { downloadDocument } from "utils/helpers/files";
import ActionButtons from "../../../../../../Projects/Detail/Templates/Manual/Lafourgale/Components/ActionButtons";
import InfoBox from "../../../../../../Projects/Detail/Templates/Manual/Lafourgale/Components/InfoBox";

const View = ({ subscription, totalPrice, translation }) => {
  const navigate = useNavigate();
  const telecoms = get(subscription, "project.contact.telecoms", []);
  const email = get(
    find(telecoms, { system: CONTACT_POINT_SYSTEM.EMAIL }),
    "value",
    "",
  );
  const projectNumber = get(subscription, "id", "");

  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const TRANSLATION = t("DETAIL.TEMPLATES.LAFOURGALE.SUBSCRIPTION_DONE", {
    returnObjects: true,
  });

  const { t: tContract } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION_TRANSLATION = tContract("SUBSCRIPTION", {
    returnObjects: true,
  });

  const externalManagement = get(subscription, "externalManagement") || {};
  const sendAttemptAtRef = useRef(null);
  const lastSentToManagement = get(externalManagement, "lastSentToManagement");
  const lastSendStatus = get(externalManagement, "lastSendStatus");
  const lastSendError = get(externalManagement, "lastSendError");
  const lastSendAttemptAt = get(externalManagement, "lastSendAttemptAt");
  const hasNeverBeenSent = !lastSentToManagement && !lastSendAttemptAt;
  const isLastSendFailure =
    lastSendStatus === SEND_TO_MANAGEMENT_SEND_STATUSES.FAILURE;
  const isLastSendSuccess =
    lastSendStatus === SEND_TO_MANAGEMENT_SEND_STATUSES.SUCCESS;
  const showResendButton = !isLastSendSuccess;

  const actions = useScreenActionPermissions({
    module: MODULES.SUBSCRIPTION,
    submodule: SUBMODULES.BROKER_DETAIL,
  });
  // const canSendToManagement = get(actions, ACTIONS.SEND_TO_MANAGEMENT);
  const canSendToManagement = true;

  const [sendToManagement, { loading: sending }] = useMutation(
    SEND_TO_MANAGEMENT,
    {
      refetchQueries: [
        {
          query: SUBSCRIPTION,
          awaitRefetchQueries: true,
          variables: { where: { id: subscription.id } },
        },
      ],
      onCompleted: (data) => {
        const sentStatus = get(
          data,
          "sendToManagement.externalManagement.lastSendStatus",
        );
        const sentAttemptAt = get(
          data,
          "sendToManagement.externalManagement.lastSendAttemptAt",
        );
        if (
          sentStatus === SEND_TO_MANAGEMENT_SEND_STATUSES.SUCCESS &&
          sentAttemptAt &&
          sentAttemptAt !== sendAttemptAtRef.current
        ) {
          notification.success({
            message: SUBSCRIPTION_TRANSLATION.SEND_TO_MANAGEMENT_SUCCESS,
            duration: 5,
          });
        }
      },
      onError: () => {
        notification.error({
          message: SUBSCRIPTION_TRANSLATION.SEND_TO_MANAGEMENT_FAILED_TOAST,
          duration: 5,
        });
      },
    },
  );

  const [getManagementAttachments, { loading: downloading }] = useLazyQuery(
    GET_MANAGEMENT_ATTACHMENTS,
    {
      onCompleted: ({ getManagementAttachments: result }) => {
        if (result) {
          downloadDocument(result);
        }
      },
      fetchPolicy: "no-cache",
    },
  );

  const triggerSendToManagement = () => {
    sendAttemptAtRef.current = get(
      subscription,
      "externalManagement.lastSendAttemptAt",
    );
    sendToManagement({
      variables: { where: { id: subscription.id } },
    });
  };

  const handleSendToManagement = () => {
    if (hasNeverBeenSent) {
      triggerSendToManagement();
      return;
    }
    Modal.confirm({
      title: SUBSCRIPTION_TRANSLATION.RESEND_CONFIRM_TITLE,
      content: SUBSCRIPTION_TRANSLATION.RESEND_CONFIRM_CONTENT,
      okText: SUBSCRIPTION_TRANSLATION.RESEND_CONFIRM_OK,
      cancelText: SUBSCRIPTION_TRANSLATION.RESEND_CONFIRM_CANCEL,
      onOk: triggerSendToManagement,
    });
  };

  const handleDownloadAttachments = () => {
    getManagementAttachments({
      variables: { where: { id: subscription.id } },
    });
  };

  const renderManagementAlert = () => {
    if (isLastSendFailure) {
      return (
        <Alert
          showIcon
          type="error"
          message={format(
            SUBSCRIPTION_TRANSLATION.SEND_TO_MANAGEMENT_FAILED_AT,
            {
              date: moment(lastSendAttemptAt || lastSentToManagement).format(
                DATE_TIME_FORMAT,
              ),
              error: lastSendError || "",
            },
          )}
        />
      );
    }
    if (isLastSendSuccess && lastSentToManagement) {
      return (
        <Alert
          showIcon
          type="success"
          message={format(
            SUBSCRIPTION_TRANSLATION.SEND_TO_MANAGEMENT_SUCCESS_AT,
            {
              date: moment(lastSentToManagement).format(DATE_TIME_FORMAT),
            },
          )}
        />
      );
    }
    if (lastSentToManagement) {
      return (
        <Alert
          showIcon
          type="info"
          message={format(
            SUBSCRIPTION_TRANSLATION.SEND_TO_MANAGEMENT_ALREADY_SENT,
            {
              date: moment(lastSentToManagement).format(DATE_TIME_FORMAT),
            },
          )}
        />
      );
    }
    return null;
  };

  const sendButtonLabel = hasNeverBeenSent
    ? SUBSCRIPTION_TRANSLATION.SEND_TO_MANAGEMENT
    : SUBSCRIPTION_TRANSLATION.RESEND_TO_MANAGEMENT;

  return (
    <div className="contact-form lf-subscription-done" key="form_area">
      <div className="contact-form__content">
        <div className="lf-subscription-done__wrapper mb-32">
          <Form>
            <div className="lf-subscription-done__header">
              <div className="lf-subscription-done__icon-wrapper">
                <MdCheckCircle size={48} color="#52c41a" />
              </div>
              <h1 className="lf-subscription-done__title">
                {TRANSLATION.TITLE}
              </h1>
              <p className="lf-subscription-done__description">
                {TRANSLATION.DESCRIPTION.replace(
                  "{{projectNumber}}",
                  projectNumber,
                )}
              </p>
            </div>

            <InfoBox
              content={
                <div className="lf-subscription-done__activations-list">
                  <div className="lf-subscription-done__activations-list__item">
                    <MdSend size={18} color="#2563eb" />
                    <span>{TRANSLATION.ACTIVATIONS_TITLE}</span>
                  </div>
                  <div className="lf-subscription-done__activations-list__item">
                    <MdCheckCircle size={16} color="#52c41a" />
                    <span>{TRANSLATION.API_LAFOURGALE}</span>
                  </div>
                  <div className="lf-subscription-done__activations-list__item">
                    <MdCheckCircle size={16} color="#52c41a" />
                    <span>
                      {TRANSLATION.API_STRIPE.replace(
                        "{{amount}}",
                        totalPrice.toFixed(2),
                      )}
                    </span>
                  </div>
                  <div className="lf-subscription-done__activations-list__item">
                    <MdCheckCircle size={16} color="#52c41a" />
                    <span>
                      {TRANSLATION.EMAIL_CONFIRMATION.replace(
                        "{{email}}",
                        email,
                      )}
                    </span>
                  </div>
                </div>
              }
            />
            <div className="d-flex flex--column gap-16 mt-24">
              {renderManagementAlert()}
              {!canSendToManagement && (
                <Alert
                  showIcon
                  type="warning"
                  message={
                    SUBSCRIPTION_TRANSLATION.SEND_TO_MANAGEMENT_FORBIDDEN
                  }
                />
              )}
              <div className="subscription-signed-actions grid--2">
                {showResendButton && (
                  <Button
                    type="primary--outlined"
                    onClick={handleSendToManagement}
                    loading={sending}
                    disabled={sending || !canSendToManagement}
                    block
                  >
                    <span className="subscription-signed-action__label">
                      {sendButtonLabel}
                    </span>
                    <MdSend size={16} />
                  </Button>
                )}
                <Button
                  type="primary"
                  onClick={handleDownloadAttachments}
                  loading={downloading}
                  disabled={downloading || !canSendToManagement}
                  block
                >
                  <span className="subscription-signed-action__label">
                    {SUBSCRIPTION_TRANSLATION.DOWNLOAD_MANAGEMENT_ATTACHMENTS ||
                      "Download attachments"}
                  </span>
                  <MdCloudDownload size={16} />
                </Button>
              </div>
            </div>
          </Form>
        </div>
        <ActionButtons
          onSubmit={() => navigate(SCREENS.PRIVATE.BROKER.PROJECTS.LIST.path)}
          translation={{ NEXT: TRANSLATION.BACK_TO_DASHBOARD }}
        />
      </div>
    </div>
  );
};

export default View;
